import { ExternalLink, X } from 'lucide-react';
import { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Button } from '../../../components/ui/Button.js';
import type { ScheduleBar } from '../types.js';
import { ScheduleBarCard, shortRef } from './ScheduleBarCard.js';
import { usePopoverShell } from './usePopoverShell.js';

/**
 * #631 — a booking clicked on the schedule shows its details beside it, and the operator stays on
 * the schedule. Until #631 a click navigated straight to the booking page, so reading one detail
 * meant losing the view. The page is one button away (**Open booking**); an extension opens its
 * own booking (094 FR-011a: the bar's `booking_id` is already the booking's).
 */
export function BookingDetailsPopover({
  bar,
  carLabel,
  anchorRect,
  onOpenBooking,
  onDismiss,
}: {
  bar: ScheduleBar;
  carLabel: string;
  anchorRect: DOMRect;
  onOpenBooking: (bookingId: string) => void;
  onDismiss: () => void;
}) {
  const { boxRef, style, placed } = usePopoverShell(anchorRect, onDismiss);
  const bookingId = bar.booking_id;
  // FR-025 (from 316's T031): focus moves in only once the popover is placed — `focus()` on a
  // `visibility: hidden` element does nothing, which is how Enter once failed to reach Confirm.
  useEffect(() => {
    if (placed) boxRef.current?.querySelector<HTMLElement>('[data-open-booking]')?.focus();
  }, [placed, boxRef]);
  return createPortal(
    <div
      ref={boxRef}
      data-testid="booking-details-popover"
      // biome-ignore lint/a11y/useSemanticElements: #950 role="dialog" on a positioned panel is the intended ARIA pattern; native <dialog> is hidden until opened and brings its own positioning
      role="dialog"
      aria-label={bookingId ? `Booking ${shortRef(bookingId)} on ${carLabel}` : carLabel}
      style={style}
      className="w-72 rounded-xl border border-hairline bg-canvas p-3 shadow-xl"
    >
      <div className="mb-3 flex items-start justify-between gap-2">
        <ScheduleBarCard bar={bar} carLabel={carLabel} />
        <button
          type="button"
          onClick={onDismiss}
          aria-label="Close"
          className="text-steel hover:text-ink"
        >
          <X size={16} aria-hidden="true" />
        </button>
      </div>
      {bookingId && (
        <div className="flex justify-end">
          <Button variant="secondary" data-open-booking="" onClick={() => onOpenBooking(bookingId)}>
            <ExternalLink size={14} aria-hidden="true" />
            Open booking
          </Button>
        </div>
      )}
    </div>,
    document.body,
  );
}
